import { useState } from "react";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { Goal, GoalFormState } from "./types";
import { GoalCard } from "./components/GoalCard";
import { GoalDialog } from "./components/GoalDialog";
import { useGoalsData } from "./hooks/useGoalsData";
import { useGoalMutations } from "./hooks/useGoalMutations";
import { createFormFromGoal, defaultForm } from "./utils";

export function GoalsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [form, setForm] = useState<GoalFormState>(defaultForm());
  const [formError, setFormError] = useState<string | null>(null);
  const { activeGoals, circleNameById, circles, goalsQuery } = useGoalsData();

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingGoal(null);
    setFormError(null);
  };

  const { createMutation, patchMutation } = useGoalMutations({
    onClose: closeDialog,
    onError: setFormError,
  });

  const openCreate = () => {
    setEditingGoal(null);
    setForm(defaultForm());
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setForm(createFormFromGoal(goal));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    setFormError(null);
    if (!form.name.trim()) {
      setFormError("Goal name is required.");
      return;
    }
    if (form.slots.some((slot) => !slot.name.trim())) {
      setFormError("All slot names are required.");
      return;
    }
    if (form.slots.some((slot) => !/^\d{2}:\d{2}$/.test(slot.dueTimeLocal))) {
      setFormError("All slot times must be in HH:mm format.");
      return;
    }

    const notify = {
      notifyCircleId: form.notifyCircleId,
      notifyCircleOnMiss: form.notifyCircleId !== null,
      notifyThresholdCount: form.notifyThresholdCount,
      notifyThresholdPeriod: form.notifyThresholdPeriod,
      notifyDelayMinutes: form.notifyDelayMinutes,
      sharedCircleIds: form.sharedCircleIds,
    };
    const slots = form.slots.map((slot, index) => ({
      id: slot.id,
      name: slot.name.trim(),
      dueTimeLocal: slot.dueTimeLocal,
      sortOrder: index,
      missGraceMinutes: slot.missGraceMinutes,
      recoveryAction: slot.recoveryAction.trim() || null,
      notifySelfOnOpen: slot.notifySelfOnOpen,
      active: true,
    }));

    if (editingGoal) {
      patchMutation.mutate({
        id: editingGoal.id,
        input: { name: form.name.trim(), ...notify, slots },
      });
      return;
    }

    createMutation.mutate({
      name: form.name.trim(),
      ...notify,
      slots: form.slots.map((slot, index) => ({
        name: slot.name.trim(),
        dueTimeLocal: slot.dueTimeLocal,
        sortOrder: index,
        missGraceMinutes: slot.missGraceMinutes,
        recoveryAction: slot.recoveryAction.trim() || null,
        notifySelfOnOpen: slot.notifySelfOnOpen,
      })),
    });
  };

  const deactivate = (goal: Goal) => {
    patchMutation.mutate({ id: goal.id, input: { active: false } });
  };

  const toggleSharedCircle = (circleId: string) => {
    setForm((previous) => ({
      ...previous,
      sharedCircleIds: previous.sharedCircleIds.includes(circleId)
        ? previous.sharedCircleIds.filter((id) => id !== circleId)
        : [...previous.sharedCircleIds, circleId],
    }));
  };

  const isSaving = createMutation.isPending || patchMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Habits
          </p>
          <h1 className="text-2xl font-semibold tracking-tight">Goals</h1>
        </div>
        <Button onClick={openCreate} size="sm">
          <PlusIcon className="w-4 h-4" />
          New goal
        </Button>
      </header>

      {goalsQuery.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
      {goalsQuery.isError && <p className="text-sm text-destructive">Failed to load goals.</p>}

      {!goalsQuery.isPending && activeGoals.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center space-y-2">
          <p className="text-sm font-medium">Start small, stay steady</p>
          <p className="text-sm text-muted-foreground">
            Pick one gentle habit — like a short morning walk — and let your circle keep you honest.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {activeGoals.map((goal) => (
          <GoalCard
            key={goal.id}
            circleNameById={circleNameById}
            goal={goal}
            onArchive={deactivate}
            onEdit={openEdit}
          />
        ))}
      </div>

      <GoalDialog
        circles={circles}
        editingGoal={editingGoal}
        form={form}
        formError={formError}
        isOpen={dialogOpen}
        isSaving={isSaving}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        setForm={setForm}
        toggleSharedCircle={toggleSharedCircle}
      />
    </div>
  );
}
